import { lazy, Suspense } from "react";
import { Route, Switch } from "wouter";
import { MaintenanceBanner } from "./screens/MaintenanceBanner";
import { Landing } from "./screens/Landing";
import { JoinFlow } from "./screens/JoinFlow";
import { Room } from "./screens/Room";
import { HowToPlay } from "./screens/HowToPlay";
import { Imprint, Privacy } from "./screens/Legal";
import { Account } from "./screens/auth/Account";
import { ConfirmEmail } from "./screens/auth/ConfirmEmail";
import { ForgotPassword } from "./screens/auth/ForgotPassword";
import { Register } from "./screens/auth/Register";
import { ResetPassword } from "./screens/auth/ResetPassword";
import { SignIn } from "./screens/auth/SignIn";

/**
 * Lazy: the admin page is reachable by a handful of people and would otherwise
 * ship in the bundle every player downloads before they can create a room.
 */
const Admin = lazy(() =>
  import("./screens/admin/Admin").then((m) => ({ default: m.Admin })),
);

const SoundTest = lazy(() =>
  import("./screens/SoundTest").then((m) => ({ default: m.SoundTest })),
);

/**
 * The banner sits above the router in a flex column, and the router gets
 * `flex-1 min-h-0`. Screens keep their own `h-full` and simply receive a
 * shorter box, so the app is pushed down and shrunk rather than overlaid, and
 * the total is still exactly one viewport with nothing to scroll.
 */
export const App = () => (
  <div className="flex h-full flex-col">
    <MaintenanceBanner />
    <div className="min-h-0 flex-1">
      <Routes />
    </div>
  </div>
);

const Routes = () => (
  <Switch>
    <Route path="/r/:code" component={Landing} />
    <Route path="/join" component={JoinFlow} />
    <Route path="/room" component={Room} />

    <Route path="/how-to-play" component={HowToPlay} />
    <Route path="/imprint" component={Imprint} />
    <Route path="/privacy" component={Privacy} />
    <Route path="/test">
      <Suspense fallback={null}>
        <SoundTest />
      </Suspense>
    </Route>

    <Route path="/admin">
      <Suspense fallback={null}>
        <Admin />
      </Suspense>
    </Route>

    {/* Account screens. Each one guards itself against being open while in a
        room — see AuthShell — because that state can arrive after the screen
        has already been navigated to. */}
    <Route path="/auth/sign-in" component={SignIn} />
    <Route path="/auth/register" component={Register} />
    <Route path="/auth/confirm" component={ConfirmEmail} />
    <Route path="/auth/forgot" component={ForgotPassword} />
    <Route path="/auth/reset" component={ResetPassword} />
    <Route path="/auth/account" component={Account} />

    <Route component={Landing} />
  </Switch>
);
